import React from 'react'
import { useTranslation } from 'react-i18next'
import { Box, ButtonProps, Grid, Icon, Tooltip, Typography } from '@mui/material'
import getOr from 'lodash/fp/getOr'
import pickBy from 'lodash/fp/pickBy'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import { VinText } from '@carsys/ui'
import { IVehicleType, ResultObjectType } from '@cbgms/api'
import LicensePlate from '@cbgms/components/form/LicensePlate'
import { IGroup } from '@cbgms/base/utils/helpers'
import { ResultTitle } from './Title'
import { useOnEnterHandler } from '@carsys/ui/src/helpers/keyboard/use-on-enter-handler'
import { manufacturerToClass } from '@cbgms/components/src/utils/vehicle'
import { SearchResultVehicleIcon } from '@carsys/ui/src/icons/SearchResultVehicle'
import { useTheme } from '@mui/material/styles'
import { useFeature } from '@cbgms/base/feature-flags'
import { useTopSearchContext } from '@cbgms/components/src/top-search/context/TopSearchContext'

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      width: '100%'
    },
    pointer: {
      cursor: 'pointer'
    }
  })
)

export interface VehicleResultProps {
  Type: ResultObjectType
  Vehicle: IGroup<IVehicleType>
  selected?: boolean
  clearQuery: () => void
  primaryAction?: () => Promise<void>
  onSelect?: (vehicle: IGroup<IVehicleType>) => void
  ActionButton?: React.FC<ActionButtonProps>
  hasGroupHeader: boolean
}
export interface ActionButtonProps extends ButtonProps {
  hasMultipleMatches: boolean
}
interface StopPropagationEvent {
  stopPropagation: () => void
}
const useGetIdenticalVehicleAttributes = (vehicle: IGroup<IVehicleType>) =>
  React.useMemo(
    () =>
      pickBy((value, key) => !vehicle.Values.find(genericVehicle => getOr(undefined, key, genericVehicle) !== value), vehicle.Values[0]),
    [vehicle]
  )

export const VehicleResult: React.FC<VehicleResultProps> = props => {
  const { Vehicle, Type, primaryAction, onSelect, ActionButton, hasGroupHeader } = props
  const classes = useStyles()
  const { t } = useTranslation()
  const isTopbarUXChangeForTimerEnabled = useFeature('topbar-ux-change-for-timer')
  const identicalVehicleAttributes = useGetIdenticalVehicleAttributes(Vehicle)
  const theme = useTheme()
  const { showSearchFilter: isNotPortal } = useTopSearchContext()
  const gridSize = React.useMemo(
    () => ({
      vehicleCell: ActionButton ? 6 : 12,
      actionCell: ActionButton ? 6 : 0
    }),
    [ActionButton]
  )
  const handleSelection = (e: StopPropagationEvent) => {
    if (onSelect) {
      onSelect(Vehicle)
      return
    }
    e && e.stopPropagation()
    primaryAction?.()
  }
  useOnEnterHandler(props.selected, handleSelection)

  const firstVehicle = Vehicle.Values[0]
  const hasMultipleMatches = Vehicle.Values.length > 1

  return (
    <div className={classes.root}>
      <Grid
        container
        display='flex'
        flexWrap={isTopbarUXChangeForTimerEnabled && isNotPortal ? 'wrap' : 'nowrap'}
        onClick={handleSelection}
        justifyContent='space-between'
      >
        <ResultTitle resultType={Type} isTitleVisible={!hasGroupHeader} />
        <Grid
          item
          xs={isTopbarUXChangeForTimerEnabled ? (isNotPortal ? 12 : 4) : 7}
          lg={isTopbarUXChangeForTimerEnabled && isNotPortal ? 6 : 7}
        >
          <Box display='flex' alignItems='flex-start'>
            <SearchResultVehicleIcon fontSize='small' sx={theme => ({ mr: 0.5, color: theme.palette.LKQGrey.background.modest })} />
            <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
              {isTopbarUXChangeForTimerEnabled ? (
                <Box display='block'>
                  <Tooltip
                    title={`${identicalVehicleAttributes?.Manufacturer} ${identicalVehicleAttributes?.Model || ''} ${
                      identicalVehicleAttributes?.Type || ''
                    }`}
                  >
                    <Typography noWrap sx={{ display: 'block', textOverflow: 'ellipsis' }}>
                      {`${identicalVehicleAttributes?.Manufacturer} ${identicalVehicleAttributes?.Model || ''} ${
                        identicalVehicleAttributes?.Type || ''
                      }`}
                    </Typography>
                  </Tooltip>
                </Box>
              ) : (
                <Box display='block' sx={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
                  <Typography noWrap sx={{ textOverflow: 'ellipsis' }}>
                    {`${identicalVehicleAttributes?.Manufacturer} ${identicalVehicleAttributes?.Model || ''} ${
                      identicalVehicleAttributes?.Type || ''
                    }`}
                  </Typography>
                </Box>
              )}
              {hasMultipleMatches && (
                <Box>
                  <Typography variant='body2' sx={{ color: theme.palette.LKQGrey.background.modest, fontStyle: 'italic' }}>
                    {Vehicle.Values.length} {t('vehicle.matching_vehicles_found')}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Grid>
        <Grid
          item
          xs={isTopbarUXChangeForTimerEnabled ? (isNotPortal ? 12 : 8) : 5}
          lg={isTopbarUXChangeForTimerEnabled && isNotPortal ? 6 : 5}
          display='flex'
          justifyContent='flex-end'
        >
          {firstVehicle.Manufacturer && (
            <Icon
              className={`icon vehicle brand ${manufacturerToClass(firstVehicle.Manufacturer)}`}
              fontSize='medium'
              sx={{ marginRight: 0.5 }}
            />
          )}
          {firstVehicle.Registration ? (
            <LicensePlate
              registration={firstVehicle.Registration}
              type={firstVehicle.AssignedAs}
              vehicleTypeIconColor={theme.palette.LKQGrey.background.modest}
            />
          ) : identicalVehicleAttributes.Vin ? (
            <VinText value={identicalVehicleAttributes.Vin} />
          ) : null}
        </Grid>
        {!!ActionButton && (
          <Grid item xs={gridSize.actionCell}>
            <Box height='100%' justifyContent='end' display='flex' alignItems='center'>
              <ActionButton hasMultipleMatches={hasMultipleMatches} onClick={handleSelection} />
            </Box>
          </Grid>
        )}
      </Grid>
    </div>
  )
}
